import React, { useRef, useEffect, useCallback } from 'react'
import * as d3 from 'd3'
import { format, parseISO } from 'date-fns'
import { BiDesempenhoCursosStore, BiCourseInfo, BiCursoPoint, CursoMetric } from './BiDesempenhoCursos'

const MARGIN = { top: 20, right: 20, bottom: 48, left: 70 }
const HEIGHT = 480

export const CURSO_COLORS: Record<number, string> = {
  1:  '#3b82f6',  // RECICLAGEM
  18: '#06b6d4',  // PREVENTIVO_RECICLAGEM
  3:  '#8b5cf6',  // COLETIVO_DE_PASSAGEIROS
  4:  '#f59e0b',  // ESCOLAR
  5:  '#ef4444',  // PRODUTOS_PERIGOSOS
  6:  '#10b981',  // EMERGENCIA
  7:  '#f97316',  // CARGA_INDIVISIVEL
  8:  '#6366f1',  // ATUALIZACAO_COLETIVO
  9:  '#d97706',  // ATUALIZACAO_ESCOLAR
  10: '#dc2626',  // ATUALIZACAO_PRODUTOS_PERIGOSOS
  11: '#059669',  // ATUALIZACAO_EMERGENCIA
  12: '#ea580c',  // ATUALIZACAO_CARGA_INDIVISIVEL
  13: '#7c3aed',  // MOTOTAXISTA
  14: '#0891b2',  // MOTOFRETISTA
  16: '#9333ea',  // ATUALIZACAO_MOTOTAXISTA
  17: '#0e7490',  // ATUALIZACAO_MOTOFRETISTA
  25: '#16a34a',  // DIRECAO_DEFENSIVA
  23: '#ca8a04',  // ATUALIZACAO_RENOVACAO_CNH
  20: '#b45309',  // NR_20_BASICO
  40: '#92400e',  // NR_20_INTERMEDIARIO
  39: '#7f1d1d',  // NR_35_TRABALHO_EM_ALTURA
  42: '#1d4ed8',  // TAXISTA
  43: '#15803d',  // MOTORISTA_APP
}
const FALLBACK_COLOR = '#94a3b8'

export function DesempenhoCursosChart() {
  const { courses, points, selectedCursoIds, metric, granularity } = BiDesempenhoCursosStore.useState()
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const coursesRef = useRef(courses)
  const pointsRef = useRef(points)
  const selectedCursoIdsRef = useRef(selectedCursoIds)
  const metricRef = useRef(metric)
  const granularityRef = useRef(granularity)

  coursesRef.current = courses
  pointsRef.current = points
  selectedCursoIdsRef.current = selectedCursoIds
  metricRef.current = metric
  granularityRef.current = granularity

  const draw = useCallback(() => {
    const courses = coursesRef.current
    const points = pointsRef.current
    const selectedCursoIds = selectedCursoIdsRef.current
    const metric = metricRef.current
    const granularity = granularityRef.current

    if (!svgRef.current || !containerRef.current) return

    const visibleCourses = courses.filter((c) => selectedCursoIds.includes(c.id))
    const visiblePoints = points.filter((p) => selectedCursoIds.includes(p.cursoId))

    svgRef.current.setAttribute('width', '0')
    const width = containerRef.current.clientWidth
    const innerWidth = width - MARGIN.left - MARGIN.right
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', HEIGHT)

    if (visiblePoints.length === 0) return

    const allBuckets = Array.from(new Set(visiblePoints.map((p) => p.bucket))).sort()

    const pointsMap = buildPointsMap(visiblePoints, metric)

    const xScale = d3.scalePoint<string>().domain(allBuckets).range([0, innerWidth]).padding(0.1)
    const maxValue = Math.max(...visiblePoints.map((p) => p[metric]), 0)
    const yScale = d3.scaleLinear().domain([0, maxValue * 1.1 || 1]).range([innerHeight, 0])

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`)

    const tickValues = everyNthBucket(allBuckets, 20)
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3.axisBottom(xScale)
          .tickValues(tickValues)
          .tickFormat((b) => formatBucketLabel(b, granularity))
      )
      .selectAll('text')
      .attr('font-size', 11)
      .attr('transform', 'rotate(-35)')
      .attr('text-anchor', 'end')

    g.append('g')
      .call(
        d3.axisLeft(yScale)
          .ticks(5)
          .tickFormat((v) => formatTickValue(Number(v), metric))
      )
      .attr('font-size', 11)

    for (const course of visibleCourses) {
      const courseMap = pointsMap.get(course.id) ?? new Map<string, number>()
      const lineData = allBuckets.filter((b) => courseMap.has(b))
      if (lineData.length === 0) continue

      g.append('path')
        .datum(lineData)
        .attr('fill', 'none')
        .attr('stroke', CURSO_COLORS[course.id] ?? FALLBACK_COLOR)
        .attr('stroke-width', 2)
        .attr('d',
          d3.line<string>()
            .x((b) => xScale(b)!)
            .y((b) => yScale(courseMap.get(b) ?? 0))
            .curve(d3.curveLinear)
        )
    }

    const verticalLine = g
      .append('line')
      .attr('stroke', '#bbb')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .style('display', 'none')

    const dots = new Map<number, d3.Selection<SVGCircleElement, unknown, null, undefined>>()
    for (const course of visibleCourses) {
      dots.set(
        course.id,
        g.append('circle')
          .attr('r', 4)
          .attr('fill', CURSO_COLORS[course.id] ?? FALLBACK_COLOR)
          .attr('stroke', '#fff')
          .attr('stroke-width', 2)
          .style('display', 'none')
      )
    }

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .on('mousemove', function (event) {
        const [mx] = d3.pointer(event)
        const closestBucket = findClosestBucket(mx, allBuckets, xScale)
        if (!closestBucket) return

        const x = xScale(closestBucket)!
        verticalLine.attr('x1', x).attr('x2', x).style('display', null)

        for (const course of visibleCourses) {
          const courseMap = pointsMap.get(course.id) ?? new Map<string, number>()
          const value = courseMap.get(closestBucket)
          const dot = dots.get(course.id)
          if (dot && value !== undefined) {
            dot.attr('cx', x).attr('cy', yScale(value)).style('display', null)
          } else if (dot) {
            dot.style('display', 'none')
          }
        }

        if (tooltipRef.current) {
          const isRightHalf = mx > innerWidth / 2
          tooltipRef.current.style.display = 'block'
          tooltipRef.current.style.left = isRightHalf
            ? `${x + MARGIN.left - 220}px`
            : `${x + MARGIN.left + 16}px`
          tooltipRef.current.style.top = `${MARGIN.top}px`
          tooltipRef.current.innerHTML = buildTooltipHTML(
            closestBucket,
            visibleCourses,
            pointsMap,
            allBuckets,
            metric,
            granularity
          )
        }
      })
      .on('mouseleave', function () {
        verticalLine.style('display', 'none')
        dots.forEach((dot) => dot.style('display', 'none'))
        if (tooltipRef.current) tooltipRef.current.style.display = 'none'
      })
  }, [])

  useEffect(() => {
    draw()
  }, [courses, points, selectedCursoIds, metric, granularity, draw])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>
    function handleResize() {
      clearTimeout(timeout)
      timeout = setTimeout(draw, 150)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', handleResize)
    }
  }, [draw])

  return (
    <div ref={containerRef} style={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
      <svg ref={svgRef} style={{ display: 'block' }} />
      <div
        ref={tooltipRef}
        style={{
          position: 'absolute',
          display: 'none',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: '8px 12px',
          fontSize: 12,
          pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          minWidth: 220,
          zIndex: 10,
        }}
      />
    </div>
  )
}

function buildPointsMap(points: BiCursoPoint[], metric: CursoMetric): Map<number, Map<string, number>> {
  const result = new Map<number, Map<string, number>>()
  for (const point of points) {
    if (!result.has(point.cursoId)) result.set(point.cursoId, new Map())
    result.get(point.cursoId)!.set(point.bucket, point[metric])
  }
  return result
}

function findClosestBucket(
  mouseX: number,
  buckets: string[],
  xScale: d3.ScalePoint<string>
): string | null {
  if (buckets.length === 0) return null
  let closest = buckets[0]
  let minDist = Math.abs(xScale(buckets[0])! - mouseX)
  for (const bucket of buckets) {
    const dist = Math.abs(xScale(bucket)! - mouseX)
    if (dist < minDist) { minDist = dist; closest = bucket }
  }
  return closest
}

function everyNthBucket(buckets: string[], maxTicks: number): string[] {
  if (buckets.length <= maxTicks) return buckets
  const step = Math.ceil(buckets.length / maxTicks)
  return buckets.filter((_, i) => i % step === 0)
}

function formatBucketLabel(bucket: string, granularity: 'daily' | 'weekly' | 'monthly'): string {
  if (granularity === 'monthly') {
    const [year, month] = bucket.split('-')
    const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    return `${MONTHS[parseInt(month) - 1]}/${year.slice(2)}`
  }
  return format(parseISO(bucket), 'dd/MM')
}

function buildTooltipHTML(
  bucket: string,
  visibleCourses: BiCourseInfo[],
  pointsMap: Map<number, Map<string, number>>,
  allBuckets: string[],
  metric: CursoMetric,
  granularity: 'daily' | 'weekly' | 'monthly'
): string {
  const bucketIndex = allBuckets.indexOf(bucket)
  const previousBucket = bucketIndex > 0 ? allBuckets[bucketIndex - 1] : null

  const rows = visibleCourses
    .map((course) => {
      const courseMap = pointsMap.get(course.id) ?? new Map<string, number>()
      const value = courseMap.get(bucket) ?? 0
      return { course, value }
    })
    .sort((a, b) => b.value - a.value)

  const dot = (color: string) =>
    `<span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0;margin-right:4px"></span>`

  const header = `<div style="font-weight:600;margin-bottom:4px">${formatBucketLabel(bucket, granularity)}</div>`

  const courseRows = rows
    .map(({ course, value }) => {
      const color = CURSO_COLORS[course.id] ?? FALLBACK_COLOR
      const courseMap = pointsMap.get(course.id) ?? new Map<string, number>()
      const previousValue = previousBucket !== null ? (courseMap.get(previousBucket) ?? 0) : null
      const delta = previousValue !== null ? value - previousValue : null

      let deltaHtml = ''
      if (delta !== null && previousValue !== null && previousValue !== 0) {
        const pct = Math.round((delta / previousValue) * 100)
        const arrow = delta >= 0 ? '▲' : '▼'
        const color = delta >= 0 ? '#22c55e' : '#ef4444'
        deltaHtml = `<span style="color:${color};margin-left:6px">${arrow} ${formatValue(Math.abs(delta), metric)} (${Math.abs(pct)}%)</span>`
      } else if (delta !== null && delta !== 0) {
        const arrow = delta >= 0 ? '▲' : '▼'
        const deltaColor = delta >= 0 ? '#22c55e' : '#ef4444'
        deltaHtml = `<span style="color:${deltaColor};margin-left:6px">${arrow} ${formatValue(Math.abs(delta), metric)}</span>`
      }

      return (
        `<div style="display:flex;align-items:center;margin-top:3px">` +
        `${dot(color)}` +
        `<span style="color:#555;font-size:11px;flex:1">${course.nomeReduzido}</span>` +
        `<span style="font-weight:500;margin-left:8px">${formatValue(value, metric)}</span>` +
        `${deltaHtml}` +
        `</div>`
      )
    })
    .join('')

  return header + courseRows
}

function formatValue(value: number, metric: CursoMetric): string {
  if (metric === 'valorNewDriverSum') {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    }).format(value)
  }
  return String(value)
}

function formatTickValue(value: number, metric: CursoMetric): string {
  if (metric === 'valorNewDriverSum') {
    return value >= 1000 ? `${(value / 1000).toFixed(0)}k` : String(value)
  }
  return String(value)
}
